import React, { useMemo } from 'react';
import { Group, Label, Tag, Text as KonvaText } from 'react-konva';
import { DEFAULT_FOUP_CAPACITY } from '../../features/equipment/equipmentConstants';
import { useSimulationInventoryCounts } from '../../hooks/useSimulationFrames';
import { useAppSelector } from '../../hooks/redux';
import { useLiveInventoryGates } from '../../hooks/useLiveInventoryDisplay';
import {
  DEFAULT_GATE_FOUP_CAPACITY,
  SYNTHETIC_ENTRY_LOAD_PORT_ID,
  SYNTHETIC_EXIT_LOAD_PORT_ID,
} from '../../utils/trackNodeAnnotations';
import { getPortInventoryAtTime } from '../../utils/simulationPlayback';
import { polygonCentroid } from '../../utils/equipmentGeometry';
import { replaySnapshotStore } from '../../store/replaySnapshotStore';
import {
  resolveDerivedGateModules,
  resolveGate2DGroupPosition,
} from '../../utils/derivedGateEquipment';

function gateInventoryAtTime(
  events: Parameters<typeof getPortInventoryAtTime>[0],
  role: 'entry' | 'exit',
  landmarkId: string | undefined,
  simulationTime: number
): number {
  const syntheticId = role === 'entry' ? SYNTHETIC_ENTRY_LOAD_PORT_ID : SYNTHETIC_EXIT_LOAD_PORT_ID;
  const syntheticCount = getPortInventoryAtTime(events, syntheticId, simulationTime);
  const landmarkCount = landmarkId ? getPortInventoryAtTime(events, landmarkId, simulationTime) : 0;
  return Math.max(syntheticCount, landmarkCount);
}

type Props = {
  zoomLevel: number;
};

type InventoryBadge = {
  id: string;
  x: number;
  y: number;
  rotation: number;
  labelX: number;
  labelY: number;
  text: string;
  isFull: boolean;
};

/** Configured FOUP capacity from stocker properties (not 3D bay slot count). */
function stockerCapacity(customProperties: Record<string, unknown> | undefined): number {
  const capacity = Number(customProperties?.foupCapacity);
  return Number.isFinite(capacity) && capacity > 0 ? capacity : DEFAULT_FOUP_CAPACITY;
}

function gateCapacity(foupCapacity: number | undefined): number {
  return typeof foupCapacity === 'number' && Number.isFinite(foupCapacity) && foupCapacity > 0
    ? foupCapacity
    : DEFAULT_GATE_FOUP_CAPACITY;
}

function toolCapacity(foupCapacity: number | undefined, fallbackCapacity?: number): number {
  if (typeof foupCapacity === 'number' && Number.isFinite(foupCapacity) && foupCapacity > 0) {
    return foupCapacity;
  }
  if (
    typeof fallbackCapacity === 'number' &&
    Number.isFinite(fallbackCapacity) &&
    fallbackCapacity > 0
  ) {
    return fallbackCapacity;
  }
  return 1;
}

/**
 * 2D FOUP inventory badges for stockers, tools, and Entry/Exit gates during simulation.
 * Visibility: JSON `metadata.inventory.showBadges` (default off).
 * Badge text is `Name: current / foupCapacity`.
 */
const SimulationInventoryOverlay: React.FC<Props> = ({ zoomLevel }) => {
  const running = useAppSelector((state) => state.simulation.running);
  const elements = useAppSelector((state) => state.layout.elements);
  const projectMetadata = useAppSelector((state) => state.layout.projectMetadata);
  const equipmentInstances = useAppSelector((state) => state.equipment.instances);
  const annotations = useAppSelector((state) => state.layout.trackNodeAnnotations);
  const simulationTime = useAppSelector((state) => state.simulation.simulationTime);
  const browserKinetic = useAppSelector((state) => state.simulation.browserKinetic);
  const replayPlan = useAppSelector((state) => state.simulation.replayPlan);
  const source = useAppSelector((state) => state.simulation.source);
  const liveGates = useLiveInventoryGates();
  const { stockerFoups, toolFoups } = useSimulationInventoryCounts();
  const gateModules = useMemo(
    () => resolveDerivedGateModules(Object.values(elements), annotations),
    [annotations, elements]
  );

  const inventoryMeta = useMemo(() => {
    if (
      projectMetadata &&
      typeof projectMetadata.inventory === 'object' &&
      projectMetadata.inventory
    ) {
      return projectMetadata.inventory as Record<string, unknown>;
    }
    return null;
  }, [projectMetadata]);

  const showBadges = useMemo(() => {
    // Default off — only show when metadata explicitly sets showBadges: true.
    return inventoryMeta?.showBadges === true;
  }, [inventoryMeta]);

  const defaultToolFoupCapacity = useMemo(() => {
    const tools =
      inventoryMeta && typeof inventoryMeta.tools === 'object' && inventoryMeta.tools
        ? (inventoryMeta.tools as Record<string, unknown>)
        : null;
    const n = Number(tools?.foupCapacity);
    return Number.isFinite(n) && n > 0 ? n : undefined;
  }, [inventoryMeta]);

  const badges = useMemo(() => {
    if (!showBadges || !running) return [] as InventoryBadge[];
    const out: InventoryBadge[] = [];
    const replaySnapshot = replayPlan ? replaySnapshotStore.getSnapshot() : null;

    for (const el of Object.values(elements)) {
      if (el.type !== 'stocker' || !el.metadata.visible) continue;
      const replayStockerIds = new Set<string>([el.id]);
      const ports = el.customProperties?.ports;
      if (Array.isArray(ports)) {
        for (const port of ports as Array<{ id?: string }>) {
          if (!port.id) continue;
          const ownerId = browserKinetic?.stockerPortOwners?.[port.id];
          if (ownerId) replayStockerIds.add(ownerId);
        }
      }
      const count = replayPlan
        ? ([...replayStockerIds]
            .map((id) => replaySnapshot?.stockers[id]?.inventory)
            .find((value): value is number => value !== undefined) ?? 0)
        : (stockerFoups[el.id] ?? 0);
      const capacity = stockerCapacity(el.customProperties);
      out.push({
        id: el.id,
        x: el.position.x,
        y: el.position.y,
        rotation: el.rotation || 0,
        labelX: el.dimensions.width / 2,
        labelY: -10 / zoomLevel,
        text: `${el.metadata.name || 'Stocker'}: ${count} / ${capacity}`,
        isFull: count >= capacity,
      });
    }

    for (const instance of Object.values(equipmentInstances)) {
      if (instance.objectType !== 'equipment') continue;
      if (!instance.polygon || instance.polygon.length < 2) continue;
      const replayToolIds = new Set<string>([instance.id, instance.name]);
      for (const port of instance.overrides?.ports ?? []) {
        const ownerId = browserKinetic?.toolPortOwners?.[port.id];
        if (ownerId) replayToolIds.add(ownerId);
      }
      const count = replayPlan
        ? ([...replayToolIds]
            .map((id) => replaySnapshot?.equipment[id]?.inventory)
            .find((value): value is number => value !== undefined) ?? 0)
        : (toolFoups[instance.id] ?? 0);
      const capacity = toolCapacity(instance.foupCapacity, defaultToolFoupCapacity);
      const centroid = polygonCentroid(instance.polygon);
      out.push({
        id: `tool-${instance.id}`,
        x: centroid.x,
        y: centroid.y,
        rotation: instance.rotation || 0,
        labelX: 0,
        labelY: -14 / zoomLevel,
        text: `${instance.name || 'Tool'}: ${count} / ${capacity}`,
        isFull: count >= capacity,
      });
    }

    if (browserKinetic) {
      const events = browserKinetic.events;

      for (const module of gateModules) {
        const liveGate = liveGates?.find((gate) => gate.portId === module.loadPortId);
        const count =
          source === 'live'
            ? Number(
                liveGate?.occupancy ??
                  (module.role === 'entry' ? liveGate?.released : liveGate?.delivered) ??
                  0
              )
            : replayPlan
              ? Math.max(
                  replaySnapshot?.ports[module.loadPortId]?.inventory ?? 0,
                  replaySnapshot?.ports[module.trackNodeId]?.inventory ?? 0
                )
              : gateInventoryAtTime(events, module.role, module.trackNodeId, simulationTime);
        const capacity = gateCapacity(module.foupCapacity);

        const gatePos = resolveGate2DGroupPosition(module);
        out.push({
          id: `gate-${module.trackNodeId}`,
          x: gatePos.x,
          y: gatePos.y,
          rotation: 0,
          labelX: 0,
          labelY: -12 / zoomLevel,
          text: `${module.name}: ${count} / ${capacity}`,
          isFull: count >= capacity,
        });
      }
    }

    return out;
  }, [
    browserKinetic,
    defaultToolFoupCapacity,
    elements,
    equipmentInstances,
    gateModules,
    liveGates,
    running,
    replayPlan,
    showBadges,
    simulationTime,
    source,
    stockerFoups,
    toolFoups,
    zoomLevel,
  ]);

  if (!running || badges.length === 0) return null;

  return (
    <Group listening={false}>
      {badges.map((badge) => (
        <Group key={badge.id} x={badge.x} y={badge.y} rotation={badge.rotation} listening={false}>
          <Label x={badge.labelX} y={badge.labelY} rotation={-badge.rotation} listening={false}>
            <Tag
              fill="rgba(15, 23, 42, 0.85)"
              stroke={badge.isFull ? '#ef4444' : '#0ea5e9'}
              strokeWidth={1 / zoomLevel}
              cornerRadius={4 / zoomLevel}
              pointerDirection="down"
              pointerWidth={8 / zoomLevel}
              pointerHeight={6 / zoomLevel}
            />
            <KonvaText
              text={badge.text}
              fill="#ffffff"
              fontSize={11 / zoomLevel}
              fontStyle="bold"
              padding={6 / zoomLevel}
              align="center"
            />
          </Label>
        </Group>
      ))}
    </Group>
  );
};

export default SimulationInventoryOverlay;
